import { Flag, MapPin, Shield, DollarSign } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useEffect, useRef, useState } from 'react';

export const UAEAdvantageSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const imageRef = useRef(null);
  const advantages = [
    {
      icon: Flag,
      title: 'Born in UAE',
      description: 'Designed in the UAE with deep understanding of local workplace culture, business practices, and employee expectations.',
    },
    {
      icon: MapPin,
      title: 'Local Data Hosting',
      description: 'Your workplace data stays in the UAE. Compliant with local regulations and optimized for regional access.',
    },
    {
      icon: Shield,
      title: 'UAE Compliance Built-In',
      description: 'Aligned with UAE labor laws, visa regulations, and local workplace standards from day one.',
    },
    {
      icon: DollarSign,
      title: 'AED Billing',
      description: 'No currency conversion complexity. Transparent AED pricing with no hidden fees or surprises.',
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (imageRef.current) {
      observer.observe(imageRef.current);
    }

    return () => {
      if (imageRef.current) {
        observer.unobserve(imageRef.current);
      }
    };
  }, []);

  return (
    <section className="py-8 sm:py-10 md:py-12 bg-background relative overflow-hidden">
      {/* UAE Flag Colors Decoration */}
      <div className="absolute top-0 left-0 right-0 h-1.5 flex">
        <div className="flex-1 bg-green-600"></div>
        <div className="flex-1 bg-white"></div>
        <div className="flex-1 bg-black"></div>
        <div className="w-6 bg-red-600"></div>
      </div>

      <div className="max-w-[745px] w-full mx-auto px-4 sm:px-6">
        {/* Content - Single Column */}
        <div className="space-y-6 sm:space-y-8 animate-fade-in">
          <div>
            <Badge className="mb-3 bg-primary/10 text-primary border-primary/20 text-xs">
              <span className="text-base mr-1.5">🇦🇪</span>
              UAE-First Platform
            </Badge>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4">
              Proudly <span className="emphasize">Made in the UAE</span>
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
              Born in the UAE. Built for the world. We understand the unique dynamics of <span className="emphasize-accent font-semibold text-foreground">UAE workplaces</span> because we're part of this ecosystem.
            </p>
          </div>

          <div className="space-y-3">
              {advantages.map((advantage, index) => {
                const Icon = advantage.icon;
                return (
                  <div
                    key={index}
                    className="flex items-start space-x-2.5 sm:space-x-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors animate-fade-in"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-sm sm:text-base font-bold mb-0.5">{advantage.title}</h3>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {advantage.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="relative">
              {/* Background layer */}
              <div className="absolute inset-0 bg-dark-section rounded-lg translate-x-0.5 translate-y-0.5"></div>
              
              {/* Main card */}
              <Card className="relative p-4 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-teal-500/10 border border-white/10">
                <div className="flex items-start space-x-2.5 sm:space-x-3">
                  <div className="text-2xl sm:text-3xl">🏛️</div>
                  <div>
                    <h3 className="text-sm sm:text-base font-bold mb-1 text-white">KSM Emirates Group LLC FZ</h3>
                    <p className="text-[10px] sm:text-xs text-gray-300 leading-relaxed">
                      Meydan Grandstand, Meydan Road<br />
                      Nad Al Sheba, Dubai, UAE
                    </p>
                  </div>
                </div>
              </Card>
            </div>

        {/* Visual - Below Content */}
        <div className="relative pt-6 sm:pt-8 animate-fade-in">
          <div 
            ref={imageRef}
            className={`relative rounded-xl overflow-hidden shadow-md max-w-2xl mx-auto transition-transform duration-1000 ease-out ${
              isVisible ? 'scale-100 opacity-100' : 'scale-90 opacity-0'
            }`}
          >
            {/* UAE Skyline Image */}
            <div className="relative h-64 sm:h-80">
              <img 
                src="https://customer-assets.emergentagent.com/job_workforce-transform/artifacts/qi5qbu5x_Gemini_Generated_Image_rdr7ilrdr7ilrdr7.png"
                alt="Dubai Skyline Illustration"
                className="w-full h-full object-cover"
              />
              
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-primary/30 via-transparent to-transparent"></div>
              
              {/* Text Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 to-transparent">
                <div className="text-center space-y-2">
                  <p className="text-lg sm:text-xl font-bold text-white">Dubai, UAE</p>
                  <p className="text-xs sm:text-sm text-gray-300">
                    Serving workplaces across the Emirates
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Floating Stats */}
          <div className="absolute bottom-16 left-0 sm:-bottom-4 sm:-left-4 bg-card rounded-lg shadow-sm p-3 sm:p-4 border-2 border-border">
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-primary mb-0.5">AED</div>
                <div className="text-[10px] text-muted-foreground">Local Currency</div>
              </div>
            </div>

            <div className="absolute -top-3 right-0 sm:-top-4 sm:-right-4 bg-card rounded-lg shadow-sm p-3 sm:p-4 border-2 border-border">
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-accent mb-0.5">24/7</div>
                <div className="text-[10px] text-muted-foreground">UAE Support</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default UAEAdvantageSection;
